import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, AlertTitle, Button, Box, Typography, Collapse, IconButton } from '@mui/material';
import { ExpandMore, ExpandLess, Refresh, Home, BugReport } from '@mui/icons-material';
import './ErrorDisplay.css';

// Error types
export type ErrorSeverity = 'error' | 'warning' | 'info';

interface ErrorDisplayProps {
  error: Error | string | null;
  severity?: ErrorSeverity;
  title?: string;
  onRetry?: () => void;
  onDismiss?: () => void;
  showDetails?: boolean;
  actions?: ReactNode;
}

// Main error display component
export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  error,
  severity = 'error',
  title,
  onRetry,
  onDismiss,
  showDetails = true,
  actions
}) => {
  const [expanded, setExpanded] = React.useState(false);

  if (!error) return null;

  const errorMessage = typeof error === 'string' ? error : error.message;
  const errorStack = typeof error === 'object' && error.stack ? error.stack : '';

  const getTitle = () => {
    if (title) return title;
    switch (severity) {
      case 'error':
        return 'An error occurred';
      case 'warning':
        return 'Warning';
      case 'info':
        return 'Information';
      default:
        return 'Notice';
    }
  };

  return (
    <Alert 
      severity={severity}
      className="error-display"
      onClose={onDismiss}
      action={
        <Box sx={{ display: 'flex', gap: 1 }}>
          {showDetails && errorStack && (
            <IconButton
              aria-expanded={expanded}
              aria-label="show more"
              onClick={() => setExpanded(!expanded)}
              size="small"
            >
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
          {onRetry && (
            <Button
              startIcon={<Refresh />}
              size="small"
              onClick={onRetry}
              color="inherit"
            >
              Retry
            </Button>
          )}
          {actions}
        </Box>
      }
    >
      <AlertTitle>{getTitle()}</AlertTitle>
      <Typography variant="body2">{errorMessage}</Typography>
      
      {showDetails && errorStack && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
            <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace' }}>
              {errorStack}
            </Typography>
          </Box>
        </Collapse>
      )}
    </Alert>
  );
};

// Error boundary component
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, errorInfo: ErrorInfo, reset: () => void) => ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    this.setState({ errorInfo });
    
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  reset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(
          this.state.error,
          this.state.errorInfo!,
          this.reset
        );
      }

      return <DefaultErrorFallback error={this.state.error} onReset={this.reset} />;
    }

    return this.props.children;
  }
}

// Default error fallback component
interface DefaultErrorFallbackProps {
  error: Error;
  onReset: () => void;
}

const DefaultErrorFallback: React.FC<DefaultErrorFallbackProps> = ({ error, onReset }) => {
  return (
    <Box className="error-boundary-fallback">
      <Box sx={{ textAlign: 'center', p: 4 }}>
        <BugReport sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
        
        <Typography variant="h5" gutterBottom>
          Something went wrong
        </Typography>
        
        <Typography variant="body1" color="text.secondary" paragraph>
          We're sorry, but something unexpected happened. Please try refreshing the page or contact support if the problem persists.
        </Typography>
        
        <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
          <AlertTitle>Error Details</AlertTitle>
          {error.message}
        </Alert>
        
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={onReset}
          >
            Try Again
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<Home />}
            onClick={() => window.location.href = '/'}
          >
            Go Home
          </Button>
        </Box>
        
        {process.env.NODE_ENV === 'development' && (
          <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.100', borderRadius: 1, textAlign: 'left' }}>
            <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace' }}>
              {error.stack}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Field-level error display
interface FieldErrorProps {
  error?: string | null;
  touched?: boolean;
}

export const FieldError: React.FC<FieldErrorProps> = ({ error, touched }) => {
  if (!error || !touched) return null;
  
  return (
    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
      {error}
    </Typography>
  );
};

// Inline error message
interface InlineErrorProps {
  message: string;
  onDismiss?: () => void;
}

export const InlineError: React.FC<InlineErrorProps> = ({ message, onDismiss }) => {
  return (
    <Box className="inline-error" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2" color="error">
        {message}
      </Typography>
      {onDismiss && (
        <IconButton size="small" onClick={onDismiss}>
          <ExpandLess />
        </IconButton>
      )}
    </Box>
  );
};

// Error list display
interface ErrorListProps {
  errors: string[];
  title?: string;
  onDismiss?: () => void;
}

export const ErrorList: React.FC<ErrorListProps> = ({ errors, title = 'Please fix the following errors:', onDismiss }) => {
  if (errors.length === 0) return null;
  
  return (
    <Alert severity="error" onClose={onDismiss}>
      <AlertTitle>{title}</AlertTitle>
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        {errors.map((error, index) => (
          <li key={index}>
            <Typography variant="body2">{error}</Typography>
          </li>
        ))}
      </ul>
    </Alert>
  );
};

// Network error display
interface NetworkErrorProps {
  onRetry: () => void;
  message?: string;
}

export const NetworkError: React.FC<NetworkErrorProps> = ({ 
  onRetry, 
  message = 'Unable to connect to the server. Please check your internet connection and try again.' 
}) => {
  return (
    <Alert 
      severity="error"
      action={
        <Button startIcon={<Refresh />} size="small" onClick={onRetry}>
          Retry
        </Button>
      }
    >
      <AlertTitle>Connection Error</AlertTitle>
      {message}
    </Alert>
  );
};

// Validation error display
interface ValidationErrorProps {
  errors: Record<string, string | string[]>;
  onDismiss?: () => void;
}

export const ValidationError: React.FC<ValidationErrorProps> = ({ errors, onDismiss }) => {
  const errorEntries = Object.entries(errors);
  
  if (errorEntries.length === 0) return null;
  
  return (
    <Alert severity="warning" onClose={onDismiss}>
      <AlertTitle>Validation Errors</AlertTitle>
      {errorEntries.map(([field, messages]) => (
        <Box key={field} sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            {field}:
          </Typography>
          {Array.isArray(messages) ? (
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {messages.map((msg, idx) => (
                <li key={idx}>
                  <Typography variant="body2">{msg}</Typography>
                </li>
              ))}
            </ul>
          ) : (
            <Typography variant="body2" sx={{ pl: 2 }}>{messages}</Typography>
          )}
        </Box>
      ))}
    </Alert>
  );
};

// Export all error components
const ErrorComponents = {
  ErrorDisplay,
  ErrorBoundary,
  FieldError,
  InlineError,
  ErrorList,
  NetworkError,
  ValidationError
};

export default ErrorComponents;